/**
 * @fileoverview Exports Highcharts with all of our addons applied.
 *
 * Highcharts v12+ modules self-register when imported (they are no longer
 * factory functions that must be called with the Highcharts instance).
 * We use require() instead of import to control execution order -- the
 * `@braze/highcharts-rounded-corners` plugin depends on a legacy
 * `Highcharts.each` shim that must be installed before it loads.
 */

import Highcharts from "highcharts/highstock";

import "@lib/globalJquery";

import { getSafeLocale } from "./getSafeLocale";
import type { ChartWithProxyProvider, ProxyGroup, ProxyProvider } from "./types";

(window as any).Highcharts = Highcharts;

if (typeof (Highcharts as any).each !== "function") {
  (Highcharts as any).each = function each<T>(
    collection: ArrayLike<T> | undefined | null,
    callback: (value: T, index: number) => void
  ): void {
    if (!collection || typeof callback !== "function") {
      return;
    }

    for (let index = 0; index < collection.length; index += 1) {
      callback(collection[index], index);
    }
  };
}

require("highcharts/highcharts-more");
require("highcharts/modules/exporting");
require("highcharts/modules/offline-exporting");
require("highcharts/modules/drilldown");
require("@braze/highcharts-rounded-corners");
require("highcharts/modules/export-data");
require("highcharts/modules/accessibility");

Highcharts.setOptions({
  accessibility: {
    // One landmark per chart, not one more for its legend and for each series.
    landmarkVerbosity: "one",
    screenReaderSection: {
      // A chart can replace its "View data table" menu item, so the screen reader's button runs that item.
      onViewDataTableClick: (event) => {
        const { target } = event;
        const chart = Highcharts.charts.find(
          (candidate) => target instanceof Node && candidate?.container.parentElement?.contains(target)
        );

        const viewData = chart?.options.exporting?.menuItemDefinitions?.viewData;

        if (chart && viewData && "onclick" in viewData) {
          viewData.onclick?.call(chart, event);
        }
      },
    },
  },
  lang: {
    // Highcharts 12 otherwise resolves each chart's locale from the closest
    // [lang] attribute and passes it to Intl.NumberFormat uncaught. The
    // server-rendered <html lang> is whitelisted, but browser translate
    // features and extensions rewrite it after load (e.g. to "auto"), which
    // crashes every chart render with a RangeError.
    locale: getSafeLocale(
      document.documentElement.lang,
      ...(window.navigator.languages ?? []),
      window.navigator.language
    ),
  },
});

/**
 * Highcharts 12.5.0 leaks the DOM event listeners registered through
 * `SVGElement#on()`.
 *
 * `SVGElement#on()` stores each binding's unbind function in `this.onEvents`,
 * but `SVGElement#destroy()` only nulls the legacy `element.onclick` /
 * `onmouseover` / ... properties and never calls those unbinders. Measured on
 * 12.5.0: a chart with N series leaks `3 * N + 1` listener registrations per
 * create/destroy cycle (the legend items' mouseover/mouseout/click plus the
 * credits' click), and a further `3 * N` on every `chart.update()`. Series
 * trackers (`path.highcharts-tracker-line`) and point markers leak the same
 * way.
 *
 * Unbind them before delegating: `destroy()` deletes every own property at the
 * end anyway, so doing this first is a no-op for the rest of teardown.
 *
 * Remove once the upstream fix lands and we bump Highcharts.
 */
const svgElementPrototype = (Highcharts as any).SVGElement?.prototype;

if (svgElementPrototype && !svgElementPrototype.__brazeDestroyUnbindsOnEvents) {
  const originalDestroy = svgElementPrototype.destroy;

  svgElementPrototype.destroy = function destroy(this: any, ...args: unknown[]) {
    const onEvents = this.onEvents;

    if (onEvents) {
      for (const eventType of Object.keys(onEvents)) {
        const unbind = onEvents[eventType];
        if (typeof unbind === "function") {
          unbind();
        }
        delete onEvents[eventType];
      }
    }

    return originalDestroy.apply(this, args);
  };

  svgElementPrototype.__brazeDestroyUnbindsOnEvents = true;
}

/**
 * The accessibility module discards the proxy buttons it lays over legend items and chart
 * buttons without unbinding their listeners, and places them from a page position of the chart
 * that it caches and does not refresh when the chart moves. `ProxyProvider` is not exported, so
 * its prototype is taken from the first chart that has one.
 */
Highcharts.addEvent(Highcharts.Chart, "render", function patchProxyProvider(this: ChartWithProxyProvider) {
  const proxyProvider = this.accessibility?.proxyProvider;
  const prototype: ProxyProvider | undefined = proxyProvider && Object.getPrototypeOf(proxyProvider);
  const { clearGroup, removeGroup, destroy, updateProxyElementPositions } = prototype ?? {};

  if (
    !prototype ||
    prototype.__brazeUnbindsProxyListeners ||
    !clearGroup ||
    !removeGroup ||
    !destroy ||
    !updateProxyElementPositions
  ) {
    return;
  }

  const unbind = (group: ProxyGroup | undefined) =>
    group?.proxyElements?.forEach((proxy) => proxy.eventProvider?.removeAddedEvents?.());

  prototype.clearGroup = function clearGroupAndUnbind(this: ProxyProvider, groupKey) {
    const group = this.groups?.[groupKey];

    unbind(group);
    if (group) {
      group.proxyElements = [];
    }
    return clearGroup.call(this, groupKey);
  };
  prototype.removeGroup = function removeGroupAndUnbind(this: ProxyProvider, groupKey) {
    unbind(this.groups?.[groupKey]);
    return removeGroup.call(this, groupKey);
  };
  prototype.destroy = function destroyAndUnbind(this: ProxyProvider) {
    Object.values(this.groups ?? {}).forEach(unbind);
    return destroy.call(this);
  };
  prototype.updateProxyElementPositions = function updatePositionsFromCurrentLayout(this: ProxyProvider) {
    delete this.chart?.pointer?.chartPosition;
    return updateProxyElementPositions.call(this);
  };
  prototype.__brazeUnbindsProxyListeners = true;
});

/**
 * Leaving fullscreen resizes the page in steps, and the accessibility module can leave its proxy
 * buttons where an intermediate layout had them. Focusing a misplaced button scrolls the chart
 * sideways, so the buttons are placed again once the resizing has stopped.
 *
 * Remove once Highcharts places them after the last resize itself.
 */
const proxyButtonsSettleDelay = 250;
let proxyButtonsTimer: number | undefined;

const placeProxyButtonsOnceSettled = () => {
  window.clearTimeout(proxyButtonsTimer);
  proxyButtonsTimer = window.setTimeout(() => {
    Highcharts.charts.forEach((chart) => {
      const proxyProvider = (chart as ChartWithProxyProvider | undefined)?.accessibility?.proxyProvider;

      if (!chart || !proxyProvider?.updateProxyElementPositions) {
        return;
      }

      proxyProvider.updateProxyElementPositions();
      [chart.container, chart.container.parentElement].forEach((element) => {
        if (element) {
          element.scrollLeft = 0;
          element.scrollTop = 0;
        }
      });
    });
  }, proxyButtonsSettleDelay);
};

window.addEventListener("resize", placeProxyButtonsOnceSettled);
document.addEventListener("fullscreenchange", placeProxyButtonsOnceSettled);

export default Highcharts;
